import {useMutation} from "@tanstack/react-query"
import React, {useState} from "react"
import {useNavigate} from "react-router-dom"
import styled from "styled-components"
import {addExternalReview} from "../api/review.api.ts"
import {routesPath} from "../conts/routes.js"
import {useToast} from "../hooks"
import {StarReview} from "../ui/base/starReview/StarReview"
import {TextArea} from "../ui/base/textArea/TextArea"
import {BlueTicket} from "../ui/icons/BlueTicket"
import {AppButton} from "./../ui/base/appButton/AppButton"
import {HelperText} from "./../ui/base/helperText/HelperText"
import {InputSignUp} from "./../ui/base/InputSignUp/InputSignUp"
import {EmailIcon} from "./../ui/icons/inputIcons/EmailIcon"
import {ProfileIcon} from "./../ui/icons/inputIcons/ProfileIcon"
import {LogoIcon} from "./../ui/icons/LogoIcon"
import {ScrollToTop} from "../ui/base/ScrollToTop";

export const Review = () => {
    const navigate = useNavigate()
    const [isSuccess, setIsSuccess] = useState(false)

    const [formData, setFormData] = useState({
        reviewer_name: "",
        reviewer_email: "",
        teacher_email: "",
        review: "",
        rating: 0,
    })

    const [formError, setFormError] = useState({
        reviewer_name: "",
        reviewer_email: "",
        teacher_email: "",
        review: "",
        rating: "",
    })

    const onFormChange = (key: string, value: string) => {
        const isError = () => {
            if (value === undefined) {
                return true
            }

            return key === "rating" ? value === 0 : value?.trim() === ""
        }

        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))

        setFormError((prevState) => ({
            ...prevState,
            [key]: isError() ? "Must be filled" : "",
        }))
    }

    const {mutate, isLoading} = useMutation({
        mutationFn: addExternalReview,
        onSuccess: () => {
            setIsSuccess(true)
        },
        onError: (err: any) => {
            useToast("error", err?.response?.data?.Message || "Error sending Review")
        },
    })

    const onSubmit = () => {
        const errors = Object.entries(formData).reduce((acc, [key, value]) => {
            if (key === "rating") {
                if (value === 0) {
                    acc[key] = "Must be filled"
                } else {
                    acc[key] = ""
                }
            } else {
                if (value?.trim() === "") {
                    acc[key] = "Must be filled"
                } else {
                    acc[key] = ""
                }
            }

            return acc
        }, {})

        // If there are any errors, set them and prevent submission
        if (Object.values(errors).some((error) => error !== "")) {
            setFormError(errors)
            return
        }

        // Clear errors
        setFormError({
            reviewer_name: "",
            reviewer_email: "",
            teacher_email: "",
            review: "",
            rating: "",
        })

        mutate(formData)
    }

    return (
        <ScrollToTop>
            <Section>
                <HeaderWrapper>
                    <LogoWrapper>
                        <LogoIcon width='154' height='29'/>
                    </LogoWrapper>
                    {isSuccess ? null : (
                        <TitleWrapper>
                            <HelperText
                                size='h3'
                                sizeMobile='large'
                                center
                                centerMobile
                                weight={600}>
                                Share your experience
                            </HelperText>
                        </TitleWrapper>
                    )}
                </HeaderWrapper>
                {isSuccess ? (
                    <Container>
                        <FormWrapper>
                            <HeaderWrapper>
                                <SuccessWrapper>
                                    <LogoWrapper>
                                        <BlueTicket/>
                                    </LogoWrapper>
                                    <HelperText
                                        size='h3'
                                        sizeMobile='large'
                                        center
                                        centerMobile
                                        weight={600}>
                                        Thank you for sharing!
                                    </HelperText>
                                    <HelperText center centerMobile>
                                        Your review has been submitted
                                    </HelperText>
                                    <AppButton
                                        style={{width: "fit-content"}}
                                        onClick={() => navigate(routesPath.main)}>
                                        Visit our Website
                                    </AppButton>
                                </SuccessWrapper>
                            </HeaderWrapper>
                        </FormWrapper>
                    </Container>
                ) : (
                    <Container>
                        <FormWrapper>
                            <NameInputGrid>
                                <InputSignUp
                                    id='reviewer_name'
                                    placeholder='Your Name'
                                    icon={<ProfileIcon/>}
                                    value={formData.reviewer_name}
                                    setValue={(input) => onFormChange("reviewer_name", input)}
                                    isError={formError.reviewer_name}
                                    errorText={formError.reviewer_name}
                                    placeholderColor='txt'
                                />
                                <InputSignUp
                                    id='reviewer_email'
                                    value={formData.reviewer_email}
                                    placeholder='Your Email'
                                    icon={<EmailIcon/>}
                                    setValue={(input: string) =>
                                        onFormChange("reviewer_email", input.trim())
                                    }
                                    isError={formError.reviewer_email}
                                    errorText={formError.reviewer_email}
                                    placeholderColor='txt'
                                />
                            </NameInputGrid>
                            <InputSignUp
                                id='teacher_email'
                                value={formData.teacher_email}
                                placeholder='Instructor Email'
                                icon={<EmailIcon/>}
                                setValue={(input: string) =>
                                    onFormChange("teacher_email", input.trim())
                                }
                                isError={formError.teacher_email}
                                errorText={formError.teacher_email}
                                placeholderColor='txt'
                            />
                            <div>
                                <StarReview
                                    label='Rating'
                                    rate={formData.rating}
                                    setRate={(rating: number) => onFormChange("rating", rating)}
                                    isError={formError.rating}
                                    errorText={formError.rating}
                                />
                            </div>
                            <TextArea
                                placeholder='Share details of your own experience...'
                                value={formData.review}
                                setValue={(text: string) => onFormChange("review", text)}
                                isError={formError.review}
                                errorText={formError.review}
                                maxLength={400}
                            />
                        </FormWrapper>

                        <AppButton onClick={onSubmit}>Submit Review</AppButton>
                    </Container>
                )}
            </Section>
        </ScrollToTop>
    )
}

const Section = styled.div`
    font-family: var(--font-secondary);
    flex-direction: column;
    padding-inline: 10px;
    margin-inline: auto;
    min-height: 100dvh;
    max-width: 980px;
    display: flex;
    gap: 20px;
    @media (min-width: 768px) {
        padding-inline: 20px;
        gap: 40px;
    }
`

const HeaderWrapper = styled.div`
    flex-direction: column;
    padding-top: 30px;
    display: flex;
    gap: 30px;
    @media (min-width: 768px) {
        padding-top: 70px;
        gap: 50px;
    }
`

const TitleWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`

const LogoWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`

const Container = styled.div`
    display: grid;
    gap: 20px;
    font-family: var(--font-secondary);
    padding-bottom: 1rem;
    @media (min-width: 768px) {
        gap: 40px;
        padding-bottom: 2rem;
    }
`
const FormWrapper = styled.div`
    display: grid;
    gap: 10px;
    @media (min-width: 768px) {
        gap: 20px;
    }
`
const NameInputGrid = styled.div`
    display: grid;
    gap: 10px;
    grid-template-columns: 1fr;
    align-items: start;
    @media (min-width: 768px) {
        gap: 20px;
        grid-template-columns: 1fr 1fr;
    }
`

const SuccessWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 20px;
    padding-top: 2rem;
`
